import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import http from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Ship, Hotel, Compass, Car, Stamp, ArrowRight, Calendar, Users } from "lucide-react";
import { NumberStepper } from "./NumberStepper";
import { hotelMinDate } from "@/lib/hotelAvailability";

const MS_PER_DAY = 86400000;

const TABS_DEF = [
  { key: "ferry", labelKey: "nav.ferry", icon: Ship },
  { key: "hotel", labelKey: "nav.hotel", icon: Hotel },
  { key: "tour", labelKey: "nav.tour", icon: Compass },
  { key: "car", labelKey: "nav.car", icon: Car },
  { key: "visa", labelKey: "nav.visa", icon: Stamp },
];

// Hardcoded fallback so the search form is NEVER empty even if /api/ports fails
// (slow mobile networks, blocked requests, cold start, etc.)
const FALLBACK_PORTS = [
  { code: "AYV", name: "Ayvalık", country: "TR" },
  { code: "BOD", name: "Bodrum", country: "TR" },
  { code: "CES", name: "Çeşme", country: "TR" },
  { code: "FET", name: "Fethiye", country: "TR" },
  { code: "KUS", name: "Kuşadası", country: "TR" },
  { code: "MAR", name: "Marmaris", country: "TR" },
  { code: "TUR", name: "Turgutreis", country: "TR" },
  { code: "KOS", name: "Kos", country: "GR" },
  { code: "LRS", name: "Leros", country: "GR" },
  { code: "MEI", name: "Meis", country: "GR" },
  { code: "MID", name: "Midilli", country: "GR" },
  { code: "ROD", name: "Rodos", country: "GR" },
  { code: "SAK", name: "Sakız", country: "GR" },
  { code: "SAM", name: "Samos", country: "GR" },
  { code: "SYM", name: "Symi", country: "GR" },
];

// Bazı kalkış limanlarında öncelikli hedef sabittir (ör. Bodrum'dan önce Kos)
const DEST_PRIORITY = { BOD: ["KOS"] };

const dedupeByName = (list) => {
  const seen = new Set();
  return list.filter((p) => !seen.has(p.name) && seen.add(p.name));
};

export default function SearchWidget({ defaultTab = "ferry" }) {
  const { t } = useT();
  const [tab, setTab] = useState(defaultTab);
  const [ports, setPorts] = useState(FALLBACK_PORTS);
  const [form, setForm] = useState({
    from_port: "FET", to_port: "ROD",
    date: new Date(Date.now() + MS_PER_DAY).toISOString().slice(0, 10),
    return_date: new Date(Date.now() + 8 * MS_PER_DAY).toISOString().slice(0, 10),
    check_in: (() => { const d = new Date(Date.now() + 7 * MS_PER_DAY).toISOString().slice(0, 10); const m = hotelMinDate(); return d < m ? m : d; })(),
    nights: 3,
    children_ages: [],
    round_trip: true,
    adults: 1, children: 0, infants: 0,
    island: "Sakız", days: 2,
  });
  const nav = useNavigate();

  const [connections, setConnections] = useState(null);

  const trPorts = useMemo(() => dedupeByName(ports.filter((p) => p.country === "TR")), [ports]);
  const grPorts = useMemo(() => dedupeByName(ports.filter((p) => p.country === "GR")), [ports]);
  const portByCode = useMemo(() => Object.fromEntries(ports.map((p) => [p.code, p])), [ports]);

  // Destinations reachable from the selected origin (priority pins first, then trip volume)
  const destPorts = useMemo(() => {
    const codes = connections?.[form.from_port];
    if (Array.isArray(codes) && codes.length > 0) {
      const pri = DEST_PRIORITY[form.from_port] || [];
      const ordered = [...pri.filter((c) => codes.includes(c)), ...codes.filter((c) => !pri.includes(c))];
      return dedupeByName(ordered.map((c) => portByCode[c]).filter(Boolean));
    }
    return grPorts;
  }, [connections, form.from_port, portByCode, grPorts]);

  useEffect(() => {
    http.get("/ports").then((r) => {
      if (Array.isArray(r.data) && r.data.length > 0) setPorts(r.data);
    }).catch(() => {/* keep fallback */});
    http.get("/ports/connections").then((r) => {
      if (r.data && typeof r.data === "object") setConnections(r.data);
    }).catch(() => {/* keep fallback */});
  }, []);

  // Keep to_port valid whenever origin or connection data changes
  useEffect(() => {
    if (destPorts.length > 0 && !destPorts.some((p) => p.code === form.to_port)) {
      setForm((f) => ({ ...f, to_port: destPorts[0].code }));
    }
  }, [destPorts, form.to_port]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (tab === "ferry") {
      const params = new URLSearchParams({
        from_port: form.from_port, to_port: form.to_port, date: form.date,
        adults: form.adults, children: form.children, infants: form.infants,
      });
      if (form.round_trip && form.return_date) params.set("return_date", form.return_date);
      nav(`/feribot/arama?${params.toString()}`);
    } else if (tab === "hotel") {
      const params = new URLSearchParams({
        island: form.island || "",
        check_in: form.check_in,
        nights: String(form.nights || 1),
        adults: String(form.adults || 1),
        children: String(form.children || 0),
      });
      if (form.children > 0 && Array.isArray(form.children_ages)) {
        params.set("children_ages", form.children_ages.join(","));
      }
      nav(`/otel?${params.toString()}`);
    } else if (tab === "tour") {
      nav(`/tur?island=${encodeURIComponent(form.island || "")}`);
    } else if (tab === "car") {
      nav(`/arackiralama`);
    } else if (tab === "visa") {
      nav(`/vize`);
    }
  };

  return (
    <div className="glass relative" data-testid="search-widget">
      <div className="flex items-center gap-2 md:gap-4 px-6 md:px-10 pt-4 overflow-x-auto border-b border-white/40">
        {TABS_DEF.map(({ key, labelKey, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`tab-pill flex items-center gap-2 ${tab === key ? "active" : ""}`}
            data-testid={`tab-${key}`}
          >
            <Icon className="w-4 h-4" strokeWidth={1.6} /> {t(labelKey)}
          </button>
        ))}
      </div>

      <form onSubmit={onSubmit} className="px-6 md:px-10 py-7">
        {tab === "ferry" && (
          <>
            <div className="flex gap-2 mb-4">
              <button type="button" className={`label-tiny px-3 py-1.5 border ${form.round_trip ? "bg-[hsl(var(--ink))] text-white border-[hsl(var(--ink))]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-fg))]"}`} onClick={() => setForm({ ...form, round_trip: true })} data-testid="trip-type-roundtrip">Gidiş-Dönüş</button>
              <button type="button" className={`label-tiny px-3 py-1.5 border ${!form.round_trip ? "bg-[hsl(var(--ink))] text-white border-[hsl(var(--ink))]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-fg))]"}`} onClick={() => setForm({ ...form, round_trip: false })} data-testid="trip-type-oneway">Tek Yön</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-3">
              <label className="label-tiny block mb-2 text-[hsl(var(--muted-fg))]">{t("search.from")}</label>
              <select className="input" value={form.from_port} onChange={(e) => setForm({ ...form, from_port: e.target.value })} data-testid="select-from-port">
                {trPorts.map((p) => (
                  <option key={p.code} value={p.code}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="label-tiny block mb-2 text-[hsl(var(--muted-fg))]">{t("search.to")}</label>
              <select className="input" value={form.to_port} onChange={(e) => setForm({ ...form, to_port: e.target.value })} data-testid="select-to-port">
                {destPorts.map((p) => (
                  <option key={p.code} value={p.code}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className={form.round_trip ? "md:col-span-2" : "md:col-span-2"}>
              <label className="label-tiny block mb-2 text-[hsl(var(--muted-fg))]">{t("search.date")}</label>
              <input type="date" className="input" value={form.date} min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="input-date" />
            </div>
            {form.round_trip && (
              <div className="md:col-span-2">
                <label className="label-tiny block mb-2 text-[hsl(var(--muted-fg))]">Dönüş</label>
                <input type="date" className="input" value={form.return_date} min={form.date}
                  onChange={(e) => setForm({ ...form, return_date: e.target.value })} data-testid="input-return-date" />
              </div>
            )}
            <div className={form.round_trip ? "md:col-span-12 lg:col-span-2" : "md:col-span-2"}>
              <label className="label-tiny block mb-2 text-[hsl(var(--muted-fg))]">{t("search.passengers")}</label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[11px] text-center text-[hsl(var(--muted-fg))] mb-1">Yetişkin</p>
                  <select className="input text-center" value={form.adults} aria-label="Yetişkin sayısı"
                    onChange={(e) => setForm({ ...form, adults: +e.target.value })} data-testid="input-adults">
                    {[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-[11px] text-center text-[hsl(var(--muted-fg))] mb-1">Çocuk</p>
                  <select className="input text-center" value={form.children} aria-label="Çocuk sayısı"
                    onChange={(e) => setForm({ ...form, children: +e.target.value })} data-testid="input-children">
                    {[0,1,2,3,4,5,6,7,8].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className={form.round_trip ? "md:col-span-12 lg:col-span-2" : "md:col-span-2"}>
              <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2 mt-4 md:mt-0" data-testid="btn-search-ferry">
                {t("search.searchFerry")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          </>
        )}

        {tab === "hotel" && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
              <div className="md:col-span-3">
                <label className="label-tiny block mb-2 text-[hsl(var(--muted-fg))]">Ada / Destinasyon</label>
                <select className="input" value={form.island} onChange={(e) => setForm({ ...form, island: e.target.value })} data-testid="select-island">
                  {["Sakız", "Samos", "Midilli", "Rodos", "Kos", "Santorini", "Mykonos", "Leros", "Dubai", "Çoklu Ada"].map((i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label-tiny block mb-2 text-[hsl(var(--muted-fg))]">Giriş Tarihi</label>
                <input type="date" className="input" value={form.check_in}
                  min={hotelMinDate()}
                  onChange={(e) => setForm({ ...form, check_in: e.target.value })} data-testid="input-check-in" />
              </div>
              <div className="md:col-span-2">
                <label className="label-tiny block mb-2 text-[hsl(var(--muted-fg))]">Gece Sayısı</label>
                <NumberStepper min={1} max={30} value={form.nights}
                  onChange={(val) => setForm({ ...form, nights: val })} testId="input-nights" />
              </div>
              <div className="md:col-span-2">
                <label className="label-tiny block mb-2 text-[hsl(var(--muted-fg))]">Kişiler</label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[11px] text-center text-[hsl(var(--muted-fg))] mb-1">Yetişkin</p>
                    <select className="input text-center" value={form.adults} aria-label="Otel yetişkin sayısı"
                      onChange={(e) => setForm({ ...form, adults: +e.target.value })} data-testid="hotel-adults">
                      {[1,2,3,4,5,6,7,8].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-[11px] text-center text-[hsl(var(--muted-fg))] mb-1">Çocuk</p>
                    <select className="input text-center" value={form.children} aria-label="Otel çocuk sayısı"
                      onChange={(e) => {
                        const n = Math.max(0, +e.target.value || 0);
                        const ages = [...(form.children_ages || [])];
                        while (ages.length < n) ages.push(5);
                        while (ages.length > n) ages.pop();
                        setForm({ ...form, children: n, children_ages: ages });
                      }} data-testid="hotel-children">
                      {[0,1,2,3,4,5,6].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div className="md:col-span-3">
                <button type="submit" className="btn-primary w-full" data-testid="btn-search-hotel">{t("search.searchHotel")}</button>
              </div>
            </div>
            {form.children > 0 && (
              <div className="flex flex-wrap gap-2 items-center text-xs">
                <span className="text-[hsl(var(--muted-fg))]">Çocuk yaşları:</span>
                {(form.children_ages || []).map((age, i) => (
                  <NumberStepper key={`hca-${i}`} compact min={0} max={17} value={age}
                    onChange={(val) => {
                      const ages = [...form.children_ages];
                      ages[i] = val;
                      setForm({ ...form, children_ages: ages });
                    }} testId={`hotel-child-age-${i}`} />
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "tour" && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-8">
              <label className="label-tiny block mb-2 text-[hsl(var(--muted-fg))]">{t("search.island")}</label>
              <select className="input" value={form.island} onChange={(e) => setForm({ ...form, island: e.target.value })} data-testid="select-island-tour">
                {["Sakız", "Samos", "Midilli", "Rodos", "Kos", "Santorini", "Mykonos", "Çoklu Ada"].map((i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-4">
              <button type="submit" className="btn-primary w-full" data-testid="btn-search-tour">{t("search.searchTour")}</button>
            </div>
          </div>
        )}

        {tab === "car" && (
          <div className="text-center py-4">
            <p className="text-[hsl(var(--muted-fg))] mb-4">Adalardaki araç filomuzu görüntüleyin.</p>
            <button type="submit" className="btn-primary" data-testid="btn-search-car">{t("search.searchCar")}</button>
          </div>
        )}

        {tab === "visa" && (
          <div className="text-center py-4">
            <p className="text-[hsl(var(--muted-fg))] mb-4">Schengen veya kapı vize seçenekleri için tıklayın.</p>
            <button type="submit" className="btn-primary" data-testid="btn-search-visa">{t("search.searchVisa")}</button>
          </div>
        )}
      </form>
    </div>
  );
}
